import { Sangha, SanghaSessionCycle } from "@app/shared/types";
import {
    firstSessionForScheduleAfterDate,
    formatTimestamp,
    getFormattedDate,
    nthMonthlyWeekdayForDate,
} from "@app/shared/utils";
import _ from "lodash";
import { DateTime } from "luxon";

const timeOfDay = (date: DateTime, timezone?: string) => getFormattedDate(date, timezone);

const dayOfWeek = (date: DateTime, timezone?: string) =>
    formatTimestamp(date.toMillis(), {
        weekday: "long",
        timeZone: timezone || date.zoneName,
    });

export const SanghaDayAndTimeDescription = (props: {
    sangha: Sangha;
    overrideStartTime?: DateTime;
    overrideEndTime?: DateTime;
    userTimeZone: string;
    renderTimeZone?: boolean;
}) => {
    const { overrideStartTime, overrideEndTime, userTimeZone, sangha, renderTimeZone } = props;

    const sanghaCycle = sangha.schedule.cycle;
    const sanghaTimeZone = sangha.schedule.timeZone;

    const getNextSession = () =>
        firstSessionForScheduleAfterDate(
            DateTime.fromISO(sangha.schedule.firstSessionDate),
            DateTime.local(),
            sanghaTimeZone,
            sanghaCycle,
        );

    const sessionStartTime = overrideStartTime || getNextSession();
    const sessionEndTime = overrideEndTime || getNextSession().plus({ minutes: sangha.duration });

    const originalDayOfWeek = dayOfWeek(sessionStartTime, sanghaTimeZone);
    const localDayOfWeek = dayOfWeek(sessionStartTime, userTimeZone);
    const localWeekdayDiffersFromOriginal = localDayOfWeek !== originalDayOfWeek;

    const localTimeOfDayStart = timeOfDay(sessionStartTime, userTimeZone);
    const localTimeOfDayEnd = timeOfDay(sessionEndTime, userTimeZone);

    const dayDescription = () => {
        if (sanghaCycle === SanghaSessionCycle.weekly) {
            return `${localDayOfWeek}s`;
        } else if (sanghaCycle === SanghaSessionCycle.monthly) {
            if (localWeekdayDiffersFromOriginal) {
                return `a ${localDayOfWeek}`;
            } else {
                const weekOfMonth = nthMonthlyWeekdayForDate(sessionStartTime);
                const weekOfMonthDisplay = {
                    1: "first",
                    2: "second",
                    3: "third",
                    4: "fourth",
                    5: "fifth",
                }[weekOfMonth];

                return `${_.upperFirst(weekOfMonthDisplay)} ${localDayOfWeek}s`;
            }
        } else if (sanghaCycle === SanghaSessionCycle.twiceMonthly) {
            if (localWeekdayDiffersFromOriginal) {
                return `a ${localDayOfWeek}`;
            } else {
                const weekOfMonth = nthMonthlyWeekdayForDate(sessionStartTime);

                const weekOfMonthDisplay = {
                    1: "first and third",
                    2: "second and fourth",
                    3: "first and third",
                    4: "second and fourth",
                    5: "",
                }[weekOfMonth];

                return `${_.upperFirst(weekOfMonthDisplay)} ${localDayOfWeek}s`;
            }
        }
    };

    const renderTimezone = () => {
        return DateTime.now().setZone(userTimeZone).offsetNameShort;
    };

    return (
        <>
            {dayDescription()}, {localTimeOfDayStart} - {localTimeOfDayEnd}{" "}
            {renderTimeZone ? renderTimezone() : undefined}
        </>
    );
};
